import type { FindingRow } from "../api/client";

/**
 * Who put a finding on the record — the one sentence the card and the export both print.
 *
 * **`findings.recorded_by` answers a narrower question than the line asks.** It is a foreign
 * key to `users`, so it is null for everything no person typed, and the card that printed it
 * raw read `— · 11:18` under a grade Complao had just proposed. The reader of that line wants
 * to know *whose* judgement this is, and ADR 0021 already holds the answer in the pair
 * `origin` + `disposition`; this composes it rather than inventing a second place for it.
 *
 * ⚠ **`policy` is not `ai_proposed` and must not read as it.** The follow-up cap's Minor NC
 * and "I don't know"'s Observation were reached by a rule counting to three — no auditor
 * decided them and no model judged them either. Naming Complao alone there would claim an
 * assessment that never happened, which is the false claim ADR 0021 exists to prevent, so
 * the automatic ones say so.
 *
 * **A disposition names the auditor beside Complao, never instead of it.** The route stamps
 * `recorded_by` on accept, amend and reject alike, so the name is always there to print by
 * then — but `origin` stays `ai_proposed`, because who originated a finding and who decided
 * it are two facts and a report claiming human oversight rests on both being legible.
 */
export function recordedBy(
  finding: FindingRow | undefined,
  payload: Record<string, unknown>,
  authorName?: string | null,
): string {
  const origin = String(finding?.origin ?? payload.origin ?? "");
  const disposition = finding?.disposition ?? null;
  const person = finding?.recorded_by ?? authorName ?? null;

  if (origin === "policy") return "Complao — automatic";
  if (origin === "ai_proposed") {
    if (disposition === null) return "Complao";
    return `Complao, ${DISPOSED[disposition] ?? "decided"} by ${person ?? "the lead auditor"}`;
  }
  return person ?? "—";
}

const DISPOSED: Record<string, string> = {
  accepted_unchanged: "accepted",
  amended: "amended",
  rejected: "rejected",
};
